import { useRef, useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

import useLayoutStore from "../../store/useLayoutStore";
import useAppStore from "../../store/useAppStore";
import useAuthStore from "../../store/useAuthStore";

const OverlapAbsenceModal = ({ onClose, overlappingItem, selectedType, onConfirmSubmit, isBolovanjeOverlap }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const modalContainerRef = useRef(null);
    const isBolovanjeSelected = selectedType === "Bolovanje";
    const isOverlappingBolovanje = overlappingItem?.type === "bolovanje";

    const formatType = (type) => {
        if (type === "godisnji_odmor") return "Godišnji odmor";
        if (type === "sluzbeni_put") return "Službeni put";
        if (type === "bolovanje") return "Bolovanje";
        return "Ostalo";
    };

    const formatStartDate = (item) => {
        if (!item) return "-";
        const day = item.dan_pocetak;
        const month = item.mesec_pocetak;
        const year = item.godina_pocetak;
        if (!day || !month || !year) return "-";
        const dd = String(day).padStart(2, "0");
        const mm = String(month).padStart(2, "0");
        return `${dd}.${mm}.${year}.`;
    };

    const formatEndDate = (item) => {
        if (!item) return "-";
        const day = item.dan_kraj;
        const month = item.mesec_kraj;
        const year = item.godina_kraj;
        if (!day || !month || !year) return "-";
        const dd = String(day).padStart(2, "0");
        const mm = String(month).padStart(2, "0");
        return `${dd}.${mm}.${year}.`;
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) return;
            onClose();
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, [onClose]);

    const modalContent = (
        <>
            <motion.div
                className="dv-edit-modal-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { delay: 0.14, duration: 0.14, ease: "easeInOut" } }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'none' }}
            >
                <motion.div
                    ref={modalContainerRef}
                    className={`new-absence-modal ${isDarkTheme ? "dark-new-absence-modal" : "white-new-absence-modal"}`}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                    style={{ pointerEvents: 'auto' }}
                >
                    <div className="absence-modal-close-buttons-container">
                        <button className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`} onClick={onClose} aria-label="Close modal">
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                                <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                    <h3>{isBolovanjeSelected ? `Prekid - ${selectedType}` : "Preklapanje datuma"}</h3>
                    <div className="new-absence-form-container">
                        <p className="na-overlap-message">
                            {isBolovanjeOverlap
                                ? "Bolovanje ne može prekinuti drugo bolovanje. Molimo kontaktirajte nadređenog."
                                : isBolovanjeSelected
                                    ? "Da li ste sigurni da želite da zaustavite trenutni zahtev i otvorite bolovanje?"
                                    : isOverlappingBolovanje
                                        ? overlappingItem?.potvrda
                                            ? "Postoji već potvrđeno bolovanje koje ima preklapanje datuma. Promena potvrđenog bolovanja nije moguća, molimo kontaktirajte nadređenog."
                                            : "Postoji već bolovanje koje ima preklapanje datuma, ali pošto nije potvrđeno, još uvek možete izmeniti postojeći zahtev."
                                        : overlappingItem?.potvrda
                                            ? "Postoji već potvrđen zahtev koji ima preklapanje datuma. Promena potvrđenog zahteva nije moguća, molimo kontaktirajte nadređenog."
                                            : "Postoji već zahtev koji ima preklapanje datuma, ali pošto nije potvrđen, još uvek možete izmeniti postojeći zahtev."}
                        </p>
                        {overlappingItem && (
                            <div className="na-overlap-request">
                                <div>- Tip: {formatType(overlappingItem.type)}</div>
                                <div>- Potvrda: {overlappingItem.potvrda ? "Da" : "Ne"}</div>
                                <div>- Datum početka: {formatStartDate(overlappingItem)}</div>
                                <div>- Datum kraja: {formatEndDate(overlappingItem)}</div>
                            </div>
                        )}
                        {isBolovanjeOverlap ? (
                            <button type="button" className={`na-submit-button ${isDarkTheme ? "dark-big-btn" : ""}`} onClick={onClose}>Uredu</button>
                        ) : isBolovanjeSelected ? (
                            <div className="na-overlap-actions">
                                <button
                                    type="button"
                                    className={` na-overlap-actions-button ${isDarkTheme ? "dark-btn-close" : ""}`}
                                    onClick={() => onClose()}
                                >
                                    Odustani
                                </button>
                                <button
                                    type="button"
                                    className={`na-submit-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                                    onClick={() => onConfirmSubmit()}
                                >
                                    Potvrdi
                                </button>
                            </div>
                        ) : (
                            <button type="button" className={`na-submit-button ${isDarkTheme ? "dark-big-btn" : ""}`} onClick={onClose}>Uredu</button>
                        )}
                    </div>
                </motion.div>
            </motion.div>
            <motion.div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'auto' }}
            />
        </>
    );

    return createPortal(modalContent, document.body);
};

/** Mapiranje API tipa odsustva na label u dropdown-u. */
const ABSENCE_TYPE_API_TO_LABEL = {
    godisnji_odmor: "Godišnji odmor",
    bolovanje: "Bolovanje",
    sluzbeni_put: "Službeni put",
    drugo: "Ostalo",
};

const getInitialStateFromEditRequest = (editRequest) => {
    if (!editRequest) return null;
    const [dateFrom, dateTo, typeLabel] = (() => {
        const from = editRequest.startDate
            ? (() => {
                const [d, m, y] = editRequest.startDate.split("-").map(Number);
                return new Date(y, m - 1, d);
            })()
            : null;
        const to =
            editRequest.endDate && editRequest.endDate !== "neodredjeno"
                ? (() => {
                    const [d, m, y] = editRequest.endDate.split("-").map(Number);
                    return new Date(y, m - 1, d);
                })()
                : null;
        const label = editRequest.type ? (ABSENCE_TYPE_API_TO_LABEL[editRequest.type] ?? "Godišnji odmor") : "Godišnji odmor";
        return [from, to, label];
    })();
    return { dateFrom, dateTo, typeLabel, userId: editRequest.user_id ?? null };
};

// Zajedničke helper funkcije
const getDateFromRef = () => {
    const date = new Date();
    date.setDate(date.getDate());
    return date;
};

const getDateToRef = (dateFrom) => {
    const defaultTo = new Date();
    defaultTo.setDate(defaultTo.getDate() + 4);
    if (dateFrom) {
        const fromTime = new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate()).getTime();
        const defaultToTime = new Date(defaultTo.getFullYear(), defaultTo.getMonth(), defaultTo.getDate()).getTime();
        if (fromTime > defaultToTime) {
            const result = new Date(dateFrom);
            result.setDate(result.getDate() + 4);
            return result;
        }
    }
    return defaultTo;
};

// Komponenta za nove zahteve
const NewAbsenceRequest = ({ onClose, onOverlapDetected }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const submitAbsenceRequest = useAppStore(state => state.submitAbsenceRequest);
    const calendarData = useAppStore(state => state.calendarData);
    const hasHR = useAppStore(state => state.hasHR);
    const userRole = useAuthStore(state => state.userRole);
    const isHR = useAuthStore(state => state.isHR);
    const currentUserId = useAuthStore(state => state.userId);
    const modalContainerRef = useRef(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);

    const defaultType = "Godišnji odmor";
    const [selectedAbsenceType, setSelectedAbsenceType] = useState(defaultType);
    const [, setDateToClearTick] = useState(0);
    const absenceTypeRef = useRef(defaultType);

    const dateFromRef = useRef(getDateFromRef());
    const dateToRef = useRef(getDateToRef(getDateFromRef()));

    // Lista zaposlenih za dropdown
    const employeesList = useMemo(() => {
        const employeesData = useAppStore.getState().employeesData;
        if (!employeesData) return [];
        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        return Object.entries(employeesData).map(([userId, employee]) => ({
            userId,
            fullName: `${employee.ime} ${employee.prezime}`,
            image: `${baseImgUrl}/employees/${employee.image}`,
            ime: employee.ime,
            prezime: employee.prezime
        }));
    }, []);

    const shouldShowEmployeeDropdown = (hasHR && isHR) || (!hasHR && userRole === 'admin');
    const selectedEmployeeRef = useRef(null);

    const handleChangeEmployee = (employee) => {
        selectedEmployeeRef.current = employee;
    };

    const handleChangeAbsenceType = (value) => {
        absenceTypeRef.current = value;
        if (value === "Bolovanje") {
            dateToRef.current = null;
        } else if (!dateToRef.current) {
            dateToRef.current = getDateToRef(dateFromRef.current);
        }
        setSelectedAbsenceType(value);
    };

    const handleChangeDateFrom = (value) => {
        dateFromRef.current = value;
        const fromDay = value ? new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime() : 0;
        const toDay = dateToRef.current ? new Date(dateToRef.current.getFullYear(), dateToRef.current.getMonth(), dateToRef.current.getDate()).getTime() : 0;
        if (fromDay && toDay && toDay < fromDay) {
            dateToRef.current = new Date(value.getFullYear(), value.getMonth(), value.getDate());
        }
    };

    const handleChangeDateTo = (value) => {
        dateToRef.current = value;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const dateFrom = dateFromRef.current;
        const dateTo = dateToRef.current;
        const absenceType = absenceTypeRef.current;
        const targetUserId = selectedEmployeeRef.current?.userId ?? currentUserId;

        // Provera preklapanja
        if (calendarData && targetUserId) {
            const newStart = new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate()).getTime();
            const newEnd = dateTo
                ? new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate()).getTime()
                : Infinity;

            const overlappingItem = calendarData.find((item) => {
                if (String(item.user_id) !== String(targetUserId)) return false;

                const existingStart = new Date(item.godina_pocetak, item.mesec_pocetak - 1, item.dan_pocetak).getTime();
                const existingEnd = item.dan_kraj != null && item.mesec_kraj != null && item.godina_kraj != null
                    ? new Date(item.godina_kraj, item.mesec_kraj - 1, item.dan_kraj).getTime()
                    : Infinity;

                return newStart <= existingEnd && existingStart <= newEnd;
            });

            if (overlappingItem) {
                // Zatvori formu i prikaži overlap modal kao posebnu komponentu
                if (onOverlapDetected) {
                    onOverlapDetected({
                        item: overlappingItem,
                        selectedType: absenceTypeRef.current,
                        isBolovanjeOverlap: absenceType === "Bolovanje" && overlappingItem.type === "bolovanje",
                        formData: {
                            dateFrom: dateFromRef.current,
                            dateTo: dateToRef.current,
                            absenceType: absenceTypeRef.current,
                            targetUserId: selectedEmployeeRef.current?.userId ?? currentUserId,
                        }
                    });
                }
                onClose();
                return;
            }
        }

        try {
            await submitAbsenceRequest({
                dateFrom,
                dateTo,
                absenceType,
                userId: targetUserId,
            });
            onClose();
        } catch (err) {
            console.error("submitAbsenceRequest failed:", err);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) return;
            onClose();
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, [onClose]);

    const modalContent = (
        <>
            <motion.div
                className="dv-edit-modal-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { delay: 0.14, duration: 0.14, ease: "easeInOut" } }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'none' }}
            >
                <motion.div
                    ref={modalContainerRef}
                    className={`new-absence-modal ${isDarkTheme ? "dark-new-absence-modal" : "white-new-absence-modal"}`}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.4 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                    style={{ pointerEvents: 'auto' }}
                >
                    <div className="absence-modal-close-buttons-container">
                        <button className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`} onClick={onClose} aria-label="Close modal">
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                                <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                    <h3>Zahtev za odsustvo</h3>
                    <div className="new-absence-form-container">
                        <form className="new-absence-form" onSubmit={handleSubmit}>
                            {shouldShowEmployeeDropdown && (
                                <div className="na-date-form-content-employee">
                                    <label>Za zaposlenog:</label>
                                    <div className="na-absence-employee-dropdown-container">
                                        <EmployeeDropdownForAbsence
                                            dropdownId="employee-absence"
                                            openDropdownId={openDropdownId}
                                            setOpenDropdownId={setOpenDropdownId}
                                            valueRef={selectedEmployeeRef}
                                            onChange={handleChangeEmployee}
                                            dropdownItems={employeesList}
                                            currentUserId={currentUserId}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="na-date-form-content-2">
                                <label>Vrsta odsustva:</label>
                                <div className="na-absence-type-dropdown-container">
                                    <DropdownType
                                        dropdownId="absence-type"
                                        openDropdownId={openDropdownId}
                                        setOpenDropdownId={setOpenDropdownId}
                                        valueRef={absenceTypeRef}
                                        onChange={handleChangeAbsenceType}
                                        dropdownItems={[
                                            "Bolovanje",
                                            "Službeni put",
                                            "Godišnji odmor",
                                            "Ostalo"
                                        ]}
                                    />
                                </div>
                            </div>
                            <div className="na-date-form-content-1">
                                <label>Datum:</label>
                                <div className="na-date-inputs-container">
                                    <CalendarDropdown
                                        label="Od:"
                                        dropdownId="date-from"
                                        openDropdownId={openDropdownId}
                                        setOpenDropdownId={setOpenDropdownId}
                                        valueRef={dateFromRef}
                                        onChange={handleChangeDateFrom}
                                    />
                                    <CalendarDropdown
                                        label="Do:"
                                        dropdownId="date-to"
                                        openDropdownId={openDropdownId}
                                        setOpenDropdownId={setOpenDropdownId}
                                        valueRef={dateToRef}
                                        onChange={handleChangeDateTo}
                                        minDateRef={dateFromRef}
                                        placeholder={selectedAbsenceType === "Bolovanje" ? "Neodredjeno" : ""}
                                        showClearButton={selectedAbsenceType === "Bolovanje" && !!dateToRef.current}
                                        onClear={() => {
                                            dateToRef.current = null;
                                            setDateToClearTick(prev => prev + 1);
                                        }}
                                    />
                                </div>
                            </div>
                            <button type="submit" className={`na-submit-button ${isDarkTheme ? "dark-big-btn" : ""}`}>Pošalji zahtev</button>
                        </form>
                    </div>
                </motion.div>
            </motion.div>
            <motion.div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'auto' }}
            />
        </>
    );

    return createPortal(modalContent, document.body);
};

// Komponenta za izmenu zahteva
const EditAbsenceRequest = ({ onClose, editRequest, onOverlapDetected }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const updateAbsenceRequest = useAppStore(state => state.updateAbsenceRequest);
    const calendarData = useAppStore(state => state.calendarData);
    const hasHR = useAppStore(state => state.hasHR);
    const userRole = useAuthStore(state => state.userRole);
    const isHR = useAuthStore(state => state.isHR);
    const currentUserId = useAuthStore(state => state.userId);
    const modalContainerRef = useRef(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);

    const initialEdit = getInitialStateFromEditRequest(editRequest);
    const defaultType = initialEdit?.typeLabel ?? "Godišnji odmor";
    const [selectedAbsenceType, setSelectedAbsenceType] = useState(defaultType);
    const [, setDateToClearTick] = useState(0);
    const absenceTypeRef = useRef(defaultType);

    const dateFromRef = useRef(initialEdit?.dateFrom ?? getDateFromRef());
    const dateToRef = useRef(initialEdit?.dateTo ?? (initialEdit?.typeLabel === "Bolovanje" ? null : getDateToRef(initialEdit?.dateFrom ?? getDateFromRef())));

    // Lista zaposlenih za dropdown
    const employeesList = useMemo(() => {
        const employeesData = useAppStore.getState().employeesData;
        if (!employeesData) return [];
        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        return Object.entries(employeesData).map(([userId, employee]) => ({
            userId,
            fullName: `${employee.ime} ${employee.prezime}`,
            image: `${baseImgUrl}/employees/${employee.image}`,
            ime: employee.ime,
            prezime: employee.prezime
        }));
    }, []);

    const shouldShowEmployeeDropdown = (hasHR && isHR) || (!hasHR && userRole === 'admin');

    // Ref za selektovanog zaposlenog (null = trenutni korisnik)
    const getInitialEmployee = () => {
        if (initialEdit?.userId) {
            const employeesData = useAppStore.getState().employeesData;
            if (employeesData && employeesData[initialEdit.userId]) {
                const employee = employeesData[initialEdit.userId];
                const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
                return {
                    userId: initialEdit.userId,
                    fullName: `${employee.ime} ${employee.prezime}`,
                    image: `${baseImgUrl}/employees/${employee.image}`,
                    ime: employee.ime,
                    prezime: employee.prezime
                };
            }
        }
        return null;
    };
    const selectedEmployeeRef = useRef(getInitialEmployee());

    const handleChangeEmployee = (employee) => {
        selectedEmployeeRef.current = employee;
    };

    const handleChangeAbsenceType = (value) => {
        absenceTypeRef.current = value;
        if (value === "Bolovanje") {
            dateToRef.current = null;
        } else if (!dateToRef.current) {
            dateToRef.current = getDateToRef(dateFromRef.current);
        }
        setSelectedAbsenceType(value);
    };

    const handleChangeDateFrom = (value) => {
        dateFromRef.current = value;
        const fromDay = value ? new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime() : 0;
        const toDay = dateToRef.current ? new Date(dateToRef.current.getFullYear(), dateToRef.current.getMonth(), dateToRef.current.getDate()).getTime() : 0;
        if (fromDay && toDay && toDay < fromDay) {
            dateToRef.current = new Date(value.getFullYear(), value.getMonth(), value.getDate());
        }
    };

    const handleChangeDateTo = (value) => {
        dateToRef.current = value;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const dateFrom = dateFromRef.current;
        const dateTo = dateToRef.current;
        const absenceType = absenceTypeRef.current;
        const targetUserId = editRequest?.user_id
            ? editRequest.user_id
            : (selectedEmployeeRef.current?.userId ?? currentUserId);

        // Provera preklapanja sa postojećim zahtevima istog korisnika
        // Preskačemo trenutni zahtev koji se menja
        if (calendarData && targetUserId) {
            const newStart = new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate()).getTime();
            const newEnd = dateTo
                ? new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate()).getTime()
                : Infinity;

            const overlappingItem = calendarData.find((item) => {
                // Preskoči trenutni zahtev koji se menja
                if (editRequest?.zahtev_id && String(item._id) === String(editRequest.zahtev_id)) {
                    return false;
                }

                // Proveri samo zahteve istog korisnika
                if (String(item.user_id) !== String(targetUserId)) return false;

                const existingStart = new Date(item.godina_pocetak, item.mesec_pocetak - 1, item.dan_pocetak).getTime();
                const existingEnd = item.dan_kraj != null && item.mesec_kraj != null && item.godina_kraj != null
                    ? new Date(item.godina_kraj, item.mesec_kraj - 1, item.dan_kraj).getTime()
                    : Infinity;

                return newStart <= existingEnd && existingStart <= newEnd;
            });

            if (overlappingItem) {
                // Zatvori formu i prikaži overlap modal kao posebnu komponentu
                if (onOverlapDetected) {
                    onOverlapDetected({
                        item: overlappingItem,
                        selectedType: absenceTypeRef.current,
                        isBolovanjeOverlap: absenceType === "Bolovanje" && overlappingItem.type === "bolovanje",
                        formData: {
                            dateFrom: dateFromRef.current,
                            dateTo: dateToRef.current,
                            absenceType: absenceTypeRef.current,
                            targetUserId: targetUserId,
                            zahtev_id: editRequest.zahtev_id,
                        }
                    });
                }
                onClose();
                return;
            }
        }

        try {
            await updateAbsenceRequest({
                zahtev_id: editRequest.zahtev_id,
                dateFrom,
                dateTo,
                absenceType,
                user_id: targetUserId !== currentUserId ? targetUserId : undefined,
            });
            onClose();
        } catch (err) {
            console.error("updateAbsenceRequest failed:", err);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) return;
            onClose();
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, [onClose]);

    const modalContent = (
        <>
            <motion.div
                className="dv-edit-modal-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { delay: 0.14, duration: 0.14, ease: "easeInOut" } }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'none' }}
            >
                <motion.div
                    ref={modalContainerRef}
                    className={`new-absence-modal ${isDarkTheme ? "dark-new-absence-modal" : "white-new-absence-modal"}`}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.4 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                    style={{ pointerEvents: 'auto' }}
                >
                    <div className="absence-modal-close-buttons-container">
                        <button className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`} onClick={onClose} aria-label="Close modal">
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                                <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                    <h3>Promena zahteva odsustva</h3>
                    <div className="new-absence-form-container">
                        <form className="new-absence-form" onSubmit={handleSubmit}>
                            {shouldShowEmployeeDropdown && (
                                <div className="na-date-form-content-employee">
                                    <label>Za zaposlenog:</label>
                                    <div className="na-absence-employee-dropdown-container">
                                        <EmployeeDropdownForAbsence
                                            dropdownId="employee-absence"
                                            openDropdownId={openDropdownId}
                                            setOpenDropdownId={setOpenDropdownId}
                                            valueRef={selectedEmployeeRef}
                                            onChange={handleChangeEmployee}
                                            dropdownItems={employeesList}
                                            currentUserId={currentUserId}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="na-date-form-content-2">
                                <label>Vrsta odsustva:</label>
                                <div className="na-absence-type-dropdown-container">
                                    <DropdownType
                                        dropdownId="absence-type"
                                        openDropdownId={openDropdownId}
                                        setOpenDropdownId={setOpenDropdownId}
                                        valueRef={absenceTypeRef}
                                        onChange={handleChangeAbsenceType}
                                        dropdownItems={[
                                            "Bolovanje",
                                            "Službeni put",
                                            "Godišnji odmor",
                                            "Ostalo"
                                        ]}
                                    />
                                </div>
                            </div>
                            <div className="na-date-form-content-1">
                                <label>Datum:</label>
                                <div className="na-date-inputs-container">
                                    <CalendarDropdown
                                        label="Od:"
                                        dropdownId="date-from"
                                        openDropdownId={openDropdownId}
                                        setOpenDropdownId={setOpenDropdownId}
                                        valueRef={dateFromRef}
                                        onChange={handleChangeDateFrom}
                                    />
                                    <CalendarDropdown
                                        label="Do:"
                                        dropdownId="date-to"
                                        openDropdownId={openDropdownId}
                                        setOpenDropdownId={setOpenDropdownId}
                                        valueRef={dateToRef}
                                        onChange={handleChangeDateTo}
                                        minDateRef={dateFromRef}
                                        placeholder={selectedAbsenceType === "Bolovanje" ? "Neodredjeno" : ""}
                                        showClearButton={selectedAbsenceType === "Bolovanje" && !!dateToRef.current}
                                        onClear={() => {
                                            dateToRef.current = null;
                                            setDateToClearTick(prev => prev + 1);
                                        }}
                                    />
                                </div>
                            </div>
                            <button type="submit" className={`na-submit-button ${isDarkTheme ? "dark-big-btn" : ""}`}>Pošalji zahtev</button>
                        </form>
                    </div>
                </motion.div>
            </motion.div>
            <motion.div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'auto' }}
            />
        </>
    );

    return createPortal(modalContent, document.body);
};

// Wrapper komponenta koja bira između NewAbsenceRequest i EditAbsenceRequest
// i proverava da li korisnik može da menja zahtev
const AbsenceModal = ({ onClose, editRequest }) => {
    const hasHR = useAppStore(state => state.hasHR);
    const userRole = useAuthStore(state => state.userRole);
    const isHR = useAuthStore(state => state.isHR);
    const currentUserId = useAuthStore(state => state.userId);
    const submitAbsenceRequest = useAppStore(state => state.submitAbsenceRequest);
    const updateAbsenceRequest = useAppStore(state => state.updateAbsenceRequest);
    const [overlapData, setOverlapData] = useState(null);

    const handleOverlapDetected = (data) => {
        setOverlapData(data);
    };

    const handleOverlapClose = () => {
        setOverlapData(null);
    };

    const handleOverlapConfirm = async () => {
        if (!overlapData) return;

        try {
            if (overlapData.formData.zahtev_id) {
                // Izmena postojećeg zahteva
                await updateAbsenceRequest({
                    zahtev_id: overlapData.formData.zahtev_id,
                    dateFrom: overlapData.formData.dateFrom,
                    dateTo: overlapData.formData.dateTo,
                    absenceType: overlapData.formData.absenceType,
                    user_id: overlapData.formData.targetUserId !== currentUserId
                        ? overlapData.formData.targetUserId
                        : undefined,
                });
            } else {
                // Novi zahtev
                await submitAbsenceRequest({
                    dateFrom: overlapData.formData.dateFrom,
                    dateTo: overlapData.formData.dateTo,
                    absenceType: overlapData.formData.absenceType,
                    userId: overlapData.formData.targetUserId || currentUserId,
                    // userId: overlapData.formData.targetUserId !== currentUserId
                    //     ? overlapData.formData.targetUserId
                    //     : undefined,
                });
            }
            setOverlapData(null);
            onClose();
        } catch (err) {
            console.error("submitAbsenceRequest/updateAbsenceRequest failed:", err);
        }
    };

    // Ako ima overlap data, prikaži overlap modal
    if (overlapData) {
        return (
            <OverlapAbsenceModal
                onClose={handleOverlapClose}
                overlappingItem={overlapData.item}
                selectedType={overlapData.selectedType}
                onConfirmSubmit={handleOverlapConfirm}
                isBolovanjeOverlap={overlapData.isBolovanjeOverlap}
            />
        );
    }

    // Ako nema editRequest, prikaži formu za novi zahtev
    if (!editRequest) {
        return <NewAbsenceRequest onClose={onClose} onOverlapDetected={handleOverlapDetected} />;
    }

    // Provera da li korisnik može da menja zahtev
    // Logika: može ako je njegov i nepotvrđen, ili ako je HR (ako firma ima HR), ili admin (ako firma nema HR)
    const canEdit = (() => {
        // Ako je zahtev potvrđen, niko ne može da ga menja
        if (editRequest.potvrda) {
            return false;
        }

        // Ako je zahtev korisnikov i nije potvrđen, može da ga menja
        if (String(editRequest.user_id) === String(currentUserId)) {
            return true;
        }

        // Ako firma ima HR, samo HR može da menja tuđe nepotvrđene zahteve
        if (hasHR && isHR) {
            return true;
        }

        // Ako firma nema HR, admin može da menja tuđe nepotvrđene zahteve
        if (!hasHR && userRole === 'admin') {
            return true;
        }

        return false;
    })();

    // Ako korisnik ne može da menja, ne prikazuj modal ili prikaži poruku
    if (!canEdit) {
        return null; // ili možete prikazati poruku da nema dozvolu
    }

    return <EditAbsenceRequest onClose={onClose} editRequest={editRequest} onOverlapDetected={handleOverlapDetected} />;
}

const CalendarDropdown = ({ label, dropdownId, openDropdownId, setOpenDropdownId, valueRef, onChange, minDateRef, placeholder, showClearButton, onClear }) => {
    const isOpen = openDropdownId === dropdownId;

    // State za trenutni mesec/godinu koji se prikazuje u kalendaru
    const [currentMonth, setCurrentMonth] = useState(() => {
        if (valueRef?.current) {
            const date = new Date(valueRef.current);
            return { month: date.getMonth(), year: date.getFullYear() };
        }
        const today = new Date();
        return { month: today.getMonth(), year: today.getFullYear() };
    });

    // Kad se otvori kalendar za "Do", prikaži mesec iz "Od" (minDateRef)
    useEffect(() => {
        if (isOpen && minDateRef?.current) {
            const d = minDateRef.current;
            setCurrentMonth({ month: d.getMonth(), year: d.getFullYear() });
        }
    }, [isOpen, minDateRef]);

    const handleToggle = () => {
        setOpenDropdownId(isOpen ? null : dropdownId);
    };

    const formatDate = (date) => {
        if (!date) return "";
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const displayValue = valueRef?.current ? formatDate(valueRef.current) : (placeholder ?? "");

    // Generisanje svih dana za trenutni mesec
    const getDaysInMonth = () => {
        const { month, year } = currentMonth;
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();

        // getDay() vraća 0 za nedelju, 1 za ponedeljak, itd.
        // Konvertujemo tako da ponedeljak bude 0
        let startingDayOfWeek = firstDay.getDay() - 1;
        if (startingDayOfWeek < 0) startingDayOfWeek = 6; // nedelja postaje 6

        const days = [];

        // Dodaj dane iz prethodnog meseca
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevYear = month === 0 ? year - 1 : year;
        const prevMonthLastDay = new Date(prevYear, prevMonth + 1, 0).getDate();

        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            days.push({
                day: prevMonthLastDay - i,
                month: prevMonth,
                year: prevYear,
                isCurrentMonth: false
            });
        }

        // Dodaj sve dane u trenutnom mesecu
        for (let day = 1; day <= daysInMonth; day++) {
            days.push({
                day: day,
                month: month,
                year: year,
                isCurrentMonth: true
            });
        }

        // Dodaj dane iz narednog meseca samo koliko je potrebno (5 ili 6 nedelja)
        const totalDays = startingDayOfWeek + daysInMonth;
        const limit = totalDays > 35 ? 42 : 35; // 42 dana = 6 nedelja, 35 dana = 5 nedelja
        const remainingDays = limit - totalDays;

        const nextMonth = month === 11 ? 0 : month + 1;
        const nextYear = month === 11 ? year + 1 : year;

        for (let day = 1; day <= remainingDays; day++) {
            days.push({
                day: day,
                month: nextMonth,
                year: nextYear,
                isCurrentMonth: false
            });
        }

        return days;
    };

    const handlePreviousMonth = (e) => {
        e.stopPropagation();
        setCurrentMonth(prev => {
            if (prev.month === 0) {
                return { month: 11, year: prev.year - 1 };
            }
            return { month: prev.month - 1, year: prev.year };
        });
    };

    const handleNextMonth = (e) => {
        e.stopPropagation();
        setCurrentMonth(prev => {
            if (prev.month === 11) {
                return { month: 0, year: prev.year + 1 };
            }
            return { month: prev.month + 1, year: prev.year };
        });
    };

    const isDayBeforeMin = (dayData) => {
        const min = minDateRef?.current ?? null;
        if (!min || !dayData) return false;
        const dayTs = new Date(dayData.year, dayData.month, dayData.day).getTime();
        const minTs = new Date(min.getFullYear(), min.getMonth(), min.getDate()).getTime();
        return dayTs < minTs;
    };

    const handleDayClick = (dayData, e) => {
        e.stopPropagation();
        if (!dayData) return;
        if (isDayBeforeMin(dayData)) return;
        const selectedDate = new Date(dayData.year, dayData.month, dayData.day);
        onChange(selectedDate);
        setOpenDropdownId(null);
    };

    const handleClearDateTo = (e) => {
        e.stopPropagation();
        onClear?.();
    };

    const monthNames = [
        "Januar", "Februar", "Mart", "April", "Maj", "Jun",
        "Jul", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar"
    ];

    const dayNames = ["Po", "Ut", "Sr", "Če", "Pe", "Su", "Ne"];

    const days = getDaysInMonth();
    const selectedDay = valueRef?.current ? new Date(valueRef.current).getDate() : null;
    const selectedMonth = valueRef?.current ? new Date(valueRef.current).getMonth() : null;
    const selectedYear = valueRef?.current ? new Date(valueRef.current).getFullYear() : null;

    return (
        <div className="na-date-input-wrapper">
            <label>{label}</label>
            <div className={`na-date-calendar-div ${isOpen ? "na-date-calendar-div-open" : ""}`} onClick={handleToggle}>
                {displayValue && (
                    <div className="na-date-calendar-value">
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.span
                                key={`date-value-${String(valueRef?.current)}`}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ duration: 0.14, ease: "easeOut" }}
                            >
                                {displayValue}
                            </motion.span>
                        </AnimatePresence>
                    </div>
                )}
                <div className="na-date-calendar-icon-wrapper">
                    <motion.img
                        key="calendar-icon"
                        src="./sidebar/Kalendar.svg"
                        alt="Calendar"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                    />
                    <AnimatePresence mode="wait" initial={false}>
                        {showClearButton && (
                            <motion.button
                                key="clear-date"
                                type="button"
                                className="na-date-calendar-clear-button"
                                onClick={(e) => handleClearDateTo(e)}
                                aria-label="Ponisti datum do"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ duration: 0.14, ease: "easeOut" }}
                            // style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                            >
                                <img src="/views/KalendarVar2.svg" alt="Kalendar" width="26" height="26" />
                                <svg className="na-date-clear-icon-helper" width="26" height="26" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                    <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                </svg>
                            </motion.button>
                        )}
                    </AnimatePresence>
                </div>
                <AnimatePresence mode="wait">
                    {isOpen && (
                        <motion.div
                            className="na-date-calendar-dropdown-content"
                            initial={{ opacity: 0.19, x: "-50%", y: -19 }}
                            animate={{ opacity: 1, x: "-50%", y: 0 }}
                            exit={{ opacity: 0.19, x: "-50%", y: -19 }}
                            transition={{ duration: 0.14, ease: "easeOut" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="na-date-calendar-header">
                                <motion.svg
                                    className="na-date-calendar-arrow na-date-calendar-arrow-left"
                                    onClick={handlePreviousMonth}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                >
                                    <path
                                        d="M15 18l-6-6 6-6"
                                        fill="none"
                                        stroke="#08293e"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </motion.svg>
                                <div className="na-date-calendar-month-year">
                                    <div>
                                        {monthNames[currentMonth.month]}
                                    </div>
                                    <div>
                                        {currentMonth.year}
                                    </div>
                                </div>
                                <motion.svg
                                    className="na-date-calendar-arrow na-date-calendar-arrow-right"
                                    onClick={handleNextMonth}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                >
                                    <path
                                        d="M9 18l6-6-6-6"
                                        fill="none"
                                        stroke="#08293e"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </motion.svg>
                            </div>
                            <div className="na-date-calendar-days-names">
                                {dayNames.map((dayName, index) => (
                                    <div key={index} className="na-date-calendar-day-name">
                                        {dayName}
                                    </div>
                                ))}
                            </div>
                            <div className="na-date-calendar-days-grid">
                                {days.map((dayData, index) => {
                                    const isSelected = dayData &&
                                        dayData.day === selectedDay &&
                                        dayData.month === selectedMonth &&
                                        dayData.year === selectedYear;
                                    const isCurrentMonth = dayData?.isCurrentMonth ?? false;
                                    const isDisabled = isDayBeforeMin(dayData);

                                    return (
                                        <div
                                            key={index}
                                            className={`na-date-calendar-day ${!dayData ? 'na-date-calendar-day-empty' : ''} ${!isCurrentMonth ? 'na-date-calendar-day-other-month' : ''} ${isSelected ? 'na-date-calendar-day-selected' : ''} ${isDisabled ? 'na-date-calendar-day-disabled' : ''}`}
                                            onClick={(e) => handleDayClick(dayData, e)}
                                        >
                                            {dayData?.day}
                                        </div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
}

const DropdownType = ({ dropdownId, openDropdownId, setOpenDropdownId, valueRef, onChange, dropdownItems }) => {
    const isOpen = openDropdownId === dropdownId;

    const handleSelectValueChange = (value) => {
        onChange(value);
        setOpenDropdownId(null);
    };

    const handleToggle = () => {
        setOpenDropdownId(isOpen ? null : dropdownId);
    };

    const displayValue = valueRef.current || "Vrsta odsustva";

    return (
        <div className={`na-absence-type-dropdown ${isOpen ? "na-absence-type-dropdown-open" : ""}`} onClick={handleToggle}>
            <div className="na-absence-type-dropdown-value">
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={`dropdown-value-${String(valueRef.current)}`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                    >
                        {displayValue}
                    </motion.span>
                </AnimatePresence>
            </div>
            <div className="na-absence-type-dropdown-icon-container">
                <motion.svg
                    key="dropdown-arrow"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1, rotate: isOpen ? 180 : 0 }}
                    exit={{ scale: 0 }}
                    transition={{ duration: 0.14, ease: "easeOut" }}
                    style={{ transformOrigin: "center", display: "block" }}
                    viewBox="0 0 24 24"
                    width="39"
                    height="39"
                >
                    <path
                        d="M7 10l5 5 5-5"
                        fill="none"
                        stroke="#08293e"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </motion.svg>
            </div>
            <AnimatePresence mode="wait">
                {isOpen && (
                    <motion.div
                        className="na-absence-type-dropdown-list"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                    >
                        {dropdownItems.map((item, i) => (
                            <div
                                key={i}
                                className="na-absence-type-dropdown-item"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectValueChange(item);
                                }}
                            >
                                {item}
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

const EmployeeDropdownForAbsence = ({ dropdownId, openDropdownId, setOpenDropdownId, valueRef, onChange, dropdownItems, currentUserId }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const isOpen = openDropdownId === dropdownId;

    const handleSelectValueChange = (employee) => {
        onChange(employee);
        setOpenDropdownId(null);
    };

    const handleToggle = () => {
        setOpenDropdownId(isOpen ? null : dropdownId);
    };

    const selectedEmployee = valueRef.current;

    // Ako nije selektovan zaposleni, prikaži podatke trenutnog korisnika
    const currentUserEmployee = !selectedEmployee && currentUserId && dropdownItems.find(emp => String(emp.userId) === String(currentUserId));

    // Prikaži ime i prezime trenutnog korisnika umesto "Trenutni korisnik"
    const displayValue = selectedEmployee ? selectedEmployee.fullName : (currentUserEmployee ? currentUserEmployee.fullName : 'Trenutni korisnik');
    const displayImage = selectedEmployee ? selectedEmployee.image : (currentUserEmployee ? currentUserEmployee.image : null);

    return (
        <div
            className={`na-absence-employee-dropdown ${isOpen ? "na-absence-employee-dropdown-open" : ""}`}
            onClick={handleToggle}
        >
            <div className="na-absence-employee-dropdown-value">
                <AnimatePresence mode="wait" initial={false}>
                    {displayImage ? (
                        <motion.img
                            key={`employee-image-${String(displayImage)}`}
                            src={displayImage}
                            alt=""
                            className="na-absence-employee-dropdown-icon"
                            crossOrigin="use-credentials"
                            onError={(e) => {
                                e.target.src = "./views/login.svg";
                            }}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.14, ease: "easeOut" }}
                        />
                    ) : currentUserEmployee ? (
                        <motion.img
                            key={`current-user-image-${String(currentUserEmployee.image)}`}
                            src={currentUserEmployee.image}
                            alt=""
                            className="na-absence-employee-dropdown-icon"
                            crossOrigin="use-credentials"
                            onError={(e) => {
                                e.target.src = "./views/login.svg";
                            }}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.14, ease: "easeOut" }}
                        />
                    ) : (
                        <motion.img
                            key="employee-image-default"
                            src="./views/NijeTu.svg"
                            alt=""
                            className={`na-absence-employee-dropdown-icon ${isDarkTheme ? "dark-filter" : ""}`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.14, ease: "easeOut" }}
                        />
                    )}
                </AnimatePresence>
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={`employee-value-${String(displayValue || "none")}`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                    >
                        {displayValue}
                    </motion.span>
                </AnimatePresence>
            </div>
            <div className="na-absence-employee-dropdown-icon-container">
                <motion.svg
                    key="dropdown-arrow"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1, rotate: isOpen ? 180 : 0 }}
                    exit={{ scale: 0 }}
                    transition={{ duration: 0.14, ease: "easeOut" }}
                    style={{ transformOrigin: "center", display: "block" }}
                    viewBox="0 0 24 24"
                    width="39"
                    height="39"
                >
                    <path
                        d="M7 10l5 5 5-5"
                        fill="none"
                        stroke="#08293e"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </motion.svg>
            </div>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="na-absence-employee-dropdown-list"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {dropdownItems.map((employee) => (
                            <div
                                key={employee.userId}
                                className="na-absence-employee-dropdown-item"
                                onClick={() => {
                                    handleSelectValueChange(employee);
                                }}
                            >
                                <img
                                    src={employee.image || "./views/login.svg"}
                                    alt=""
                                    className="na-absence-employee-dropdown-item-img"
                                    crossOrigin="use-credentials"
                                    onError={(e) => {
                                        e.target.src = "./views/login.svg";
                                    }}
                                />
                                <span>{employee.fullName}</span>
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default AbsenceModal;
